import React, { useState, useEffect, useLayoutEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import store from '../store/store';
import type { RootState } from '../store/store';
import DashboardLayout from './DashboardLayout';
import {
  collectProofingRecord,
  savesectionApicall,
  fetchCycleData,
  type ProofingCycleData as FetchedCycleData,
} from '../Services/ProofingRecord';
import type { ProofingCycleData, ProofingRowData } from '../store/ProofingSlice';
import { addOfflineData, setFetchedCycles } from '../store/ProofingSlice';
import { showOfflineSaveAlertForCategory } from '../utils/offlineAlerts';
import moment from 'moment';

const PROOFING_INITIAL_KEY = 'proofingInitialData';
const PROOFING_CYCLE_DATA_KEY = 'proofing-cycle';

interface ProofingInitialData {
  productName: string;
  executiveName: string;
  lineNumber: string;
  standardProofingTime?: string;
}

function getProofingInitial(): ProofingInitialData | null {
  try {
    const s = localStorage.getItem(PROOFING_INITIAL_KEY);
    return s ? (JSON.parse(s) as ProofingInitialData) : null;
  } catch {
    return null;
  }
}

function saveProofingInitial(data: ProofingInitialData): void {
  try {
    localStorage.setItem(PROOFING_INITIAL_KEY, JSON.stringify(data));
  } catch (e) {
    console.warn('Could not save Proofing initial data', e);
  }
}

function formatObservedTime(): string {
  return moment().format('DD/MM/YYYY hh:mm:ss A');
}

const MAX_CYCLES = 100;

const emptyStartForm = { productName: '', executiveName: '', lineNumber: '', standardProofingTime: '' };
const emptySaveForm = {
  proofingInTime: '',
  proofingOutTime: '',
  variety: '',
  prooferTemp: '',
  rhProofer: '',
};

function idx(cycle: number) {
  return Math.max(0, Math.min(cycle - 1, MAX_CYCLES - 1));
}

const Proofing: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { plantTourId } = useSelector((state: RootState) => state.planTour ?? {});
  const user = useSelector((state: RootState) => state.user?.user);
  const isOfflineStarted = useSelector((state: RootState) => state.appState?.isOfflineStarted ?? false);
  const reduxFetchedCycles = useSelector((state: RootState) => state.proofing?.fetchedCycles ?? []);

  const offlineInitial = useMemo(() => {
    try {
      const s = store.getState();
      if (!s?.appState?.isOfflineStarted) return null;
      const fetched = s?.proofing?.fetchedCycles ?? [];
      if (!Array.isArray(fetched) || fetched.length === 0) return null;
      const byCycle: ProofingCycleData[][] = Array(MAX_CYCLES).fill(null).map(() => []);
      fetched.forEach((cycleData: ProofingCycleData) => {
        const cycleNumStr = String(cycleData.cycleNum || '1').replace('Cycle-', '').trim();
        const cycleNum = parseInt(cycleNumStr, 10);
        if (isNaN(cycleNum) || cycleNum < 1) return;
        const i = idx(cycleNum);
        if (i >= 0 && i < MAX_CYCLES) {
          byCycle[i].push({
            cycleNum: String(cycleNum),
            product: cycleData.product || '',
            executiveName: cycleData.executiveName || '',
            lineNumber: cycleData.lineNumber || '',
            observedtime: cycleData.observedtime || '',
            standardProofingTime: cycleData.standardProofingTime || '',
            rows: cycleData.rows || [],
          });
        }
      });
      const completedCycles = Array(MAX_CYCLES).fill(false);
      byCycle.forEach((sessions, index) => { if (sessions.length > 0) completedCycles[index] = true; });
      const maxCycleFromData = byCycle.reduce((max, sessions, index) => (sessions.length > 0 ? Math.max(max, index + 1) : max), 0);
      return { savedCycles: byCycle, cycleCompleted: completedCycles, currentCycle: maxCycleFromData + 1 };
    } catch {
      return null;
    }
  }, []);

  const [cycleCompleted, setCycleCompleted] = useState<boolean[]>(
    () => offlineInitial?.cycleCompleted ?? Array(MAX_CYCLES).fill(false)
  );
  const [isSessionStarted, setIsSessionStarted] = useState<boolean[]>(() => Array(MAX_CYCLES).fill(false));
  const [cycleExpanded, setCycleExpanded] = useState<boolean[]>(() => Array(MAX_CYCLES).fill(false));
  const [savedCycles, setSavedCycles] = useState<ProofingCycleData[][]>(
    () => offlineInitial?.savedCycles ?? Array(MAX_CYCLES).fill(null).map(() => [])
  );
  const [showStartFormForNewProduct, setShowStartFormForNewProduct] = useState<boolean[]>(() => Array(MAX_CYCLES).fill(false));
  const [formDataByCycle, setFormDataByCycle] = useState<typeof emptyStartForm[]>(
    () => Array(MAX_CYCLES).fill(null).map(() => ({ ...emptyStartForm }))
  );
  const [observedtimeByCycle, setObservedtimeByCycle] = useState<string[]>(
    () => Array(MAX_CYCLES).fill('').map(() => '')
  );
  const [saveFormDataByCycle, setSaveFormDataByCycle] = useState<typeof emptySaveForm[]>(
    () => Array(MAX_CYCLES).fill(null).map(() => ({ ...emptySaveForm }))
  );
  const [currentCycle, setCurrentCycle] = useState(() => offlineInitial?.currentCycle ?? 1);
  const [expandedCompletedCycles, setExpandedCompletedCycles] = useState<Set<number>>(new Set());

  const setCycleCompletedAt = (cycle: number, value: boolean) =>
    setCycleCompleted((prev) => { const n = [...prev]; n[idx(cycle)] = value; return n; });
  const setIsSessionStartedAt = (cycle: number, value: boolean) =>
    setIsSessionStarted((prev) => { const n = [...prev]; n[idx(cycle)] = value; return n; });
  const setCycleExpandedAt = (cycle: number, value: boolean) =>
    setCycleExpanded((prev) => { const n = [...prev]; n[idx(cycle)] = value; return n; });
  const setSavedCyclesAt = (cycle: number, updater: (prev: ProofingCycleData[]) => ProofingCycleData[]) =>
    setSavedCycles((prev) => { const n = prev.map((a) => [...a]); n[idx(cycle)] = updater(n[idx(cycle)]); return n; });
  const setShowStartFormForNewProductAt = (cycle: number, value: boolean) =>
    setShowStartFormForNewProduct((prev) => { const n = [...prev]; n[idx(cycle)] = value; return n; });
  const setFormDataByCycleAt = (cycle: number, value: typeof emptyStartForm) =>
    setFormDataByCycle((prev) => { const n = prev.map((x) => ({ ...x })); n[idx(cycle)] = value; return n; });
  const setObservedtimeAt = (cycle: number, value: string) =>
    setObservedtimeByCycle((prev) => { const n = [...prev]; n[idx(cycle)] = value; return n; });
  const setSaveFormDataByCycleAt = (cycle: number, value: typeof emptySaveForm) =>
    setSaveFormDataByCycle((prev) => { const n = prev.map((x) => ({ ...x })); n[idx(cycle)] = value; return n; });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    cycle: number,
    form: 'start' | 'save'
  ) => {
    const { name, value } = e.target;
    const i = idx(cycle);
    if (form === 'save') {
      setSaveFormDataByCycle((prev) => {
        const n = prev.map((x) => ({ ...x }));
        n[i] = { ...n[i], [name]: value };
        return n;
      });
    } else {
      setFormDataByCycle((prev) => { const n = prev.map((x) => ({ ...x })); n[i] = { ...n[i], [name]: value }; return n; });
    }
  };

  const handleStartSession = (cycle: number = 1) => {
    const data = formDataByCycle[idx(cycle)];
    saveProofingInitial({
      productName: data.productName || '',
      executiveName: data.executiveName || '',
      lineNumber: data.lineNumber || '',
      standardProofingTime: data.standardProofingTime || '',
    });
    setObservedtimeAt(cycle, formatObservedTime());
    setSaveFormDataByCycleAt(cycle, { ...emptySaveForm });
    setIsSessionStartedAt(cycle, true);
  };

  const handleCancelSession = (cycle: number = 1) => {
    setIsSessionStartedAt(cycle, false);
    setSaveFormDataByCycleAt(cycle, { ...emptySaveForm });
  };

  const handleSaveSession = async (cycle: number = 1) => {
    if (!plantTourId) {
      alert('Plant Tour ID is missing. Please start a plant tour first.');
      return;
    }
    const i = idx(cycle);
    const startData = formDataByCycle[i];
    const saveData = saveFormDataByCycle[i] || emptySaveForm;
    const rows: ProofingRowData[] = [{
      variety: saveData.variety || '',
      proofingInTime: saveData.proofingInTime || '',
      proofingOutTime: saveData.proofingOutTime || '',
      prooferTemp: saveData.prooferTemp || '',
      rhProofer: saveData.rhProofer || '',
    }];
    const productName = (startData.productName || '').trim() || 'N/A';
    const executiveName = (startData.executiveName || '').trim() || 'N/A';
    const lineNumber = (startData.lineNumber || '').trim() || null;
    const standardProofingTime = (startData.standardProofingTime || '').trim() || null;
    const observedDateTime = observedtimeByCycle[i] || formatObservedTime();
    const userName = user?.Name || null;

    const records = rows.map((row) =>
      collectProofingRecord(
        cycle,
        productName,
        executiveName,
        lineNumber,
        observedDateTime,
        standardProofingTime,
        (row.variety || '').trim() || null,
        (row.proofingInTime || '').trim() || null,
        (row.proofingOutTime || '').trim() || null,
        (row.prooferTemp || '').trim() || null,
        (row.rhProofer || '').trim() || null,
        plantTourId,
        userName
      )
    );

    const savedSession: ProofingCycleData = {
      cycleNum: String(cycle),
      product: productName,
      executiveName,
      lineNumber: lineNumber || '',
      observedtime: observedDateTime,
      standardProofingTime: standardProofingTime || '',
      rows: rows.map((r) => ({
        variety: r.variety || '',
        proofingInTime: r.proofingInTime || '',
        proofingOutTime: r.proofingOutTime || '',
        prooferTemp: r.prooferTemp || '',
        rhProofer: r.rhProofer || '',
      })),
    };

    try {
      if (isOfflineStarted) {
        dispatch(addOfflineData({ cycleNo: cycle, records: [savedSession], timestamp: Date.now() }));
        setSavedCyclesAt(cycle, (prev) => [...prev, savedSession]);
        setIsSessionStartedAt(cycle, false);
        setShowStartFormForNewProductAt(cycle, false);
        setSaveFormDataByCycleAt(cycle, { ...emptySaveForm });
        try {
          const key = `${PROOFING_CYCLE_DATA_KEY}-${cycle}-data`;
          const existing = localStorage.getItem(key);
          const existingArr = existing ? (JSON.parse(existing) as any[]) : [];
          localStorage.setItem(key, JSON.stringify(Array.isArray(existingArr) ? [...existingArr, ...records] : records));
        } catch (_) {}
        showOfflineSaveAlertForCategory(cycle, 'Proofing');
      } else {
        const result = await savesectionApicall(records);
        if (result.success) {
          setSavedCyclesAt(cycle, (prev) => [...prev, savedSession]);
          setIsSessionStartedAt(cycle, false);
          setShowStartFormForNewProductAt(cycle, false);
          setSaveFormDataByCycleAt(cycle, { ...emptySaveForm });
        } else {
          alert(`Failed to save Cycle ${cycle}: ${result.message}`);
        }
      }
    } catch (error) {
      console.error('Error saving session:', error);
      alert(`Error: ${error instanceof Error ? error.message : 'Failed to save session.'}`);
    }
  };

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/home');
  };

  const handleCompleteCycle = (cycle: number) => {
    const sessions = savedCycles[idx(cycle)];
    if (!sessions || sessions.length === 0) {
      alert('Please save at least one product before completing the cycle.');
      return;
    }
    setCycleCompletedAt(cycle, true);
    setIsSessionStartedAt(cycle, false);
    setShowStartFormForNewProductAt(cycle, false);
    if (cycle === currentCycle) setCurrentCycle((prev) => prev + 1);
  };

  const handleAddNewProduct = (cycle: number) => {
    const initial = getProofingInitial();
    setShowStartFormForNewProductAt(cycle, true);
    setFormDataByCycleAt(cycle, {
      productName: initial?.productName ?? '',
      executiveName: initial?.executiveName ?? '',
      lineNumber: initial?.lineNumber ?? '',
      standardProofingTime: initial?.standardProofingTime ?? '',
    });
    setSaveFormDataByCycleAt(cycle, { ...emptySaveForm });
    setObservedtimeAt(cycle, formatObservedTime());
  };

  const toggleCompletedCycleExpansion = (cycleNum: number) => {
    setExpandedCompletedCycles((prev) => {
      const next = new Set(prev);
      if (next.has(cycleNum)) next.delete(cycleNum);
      else next.add(cycleNum);
      return next;
    });
  };

  const loadCycleData = async () => {
    if (!plantTourId || isOfflineStarted) return;
    try {
      const cycles = await fetchCycleData(plantTourId);
      const mapped: ProofingCycleData[] = cycles.map((c: FetchedCycleData) => ({
        cycleNum: c.cycleNum,
        product: c.product,
        executiveName: c.executiveName,
        lineNumber: c.lineNumber,
        observedtime: c.observedtime,
        standardProofingTime: c.standardProofingTime || '',
        rows: c.rows || [],
      }));
      dispatch(setFetchedCycles(mapped));
      const byCycle: ProofingCycleData[][] = Array(MAX_CYCLES).fill(null).map(() => []);
      cycles.forEach((cycleData: FetchedCycleData) => {
        const cycleNum = parseInt(cycleData.cycleNum, 10);
        const i = idx(cycleNum);
        if (i >= 0 && i < MAX_CYCLES) byCycle[i].push({
          cycleNum: cycleData.cycleNum,
          product: cycleData.product,
          executiveName: cycleData.executiveName,
          lineNumber: cycleData.lineNumber,
          observedtime: cycleData.observedtime,
          standardProofingTime: cycleData.standardProofingTime || '',
          rows: cycleData.rows || [],
        });
      });
      setSavedCycles(byCycle);
      setCycleCompleted((prev) => prev.map((v, i) => (byCycle[i].length > 0 ? true : v)));
      const maxCycleFromData = cycles.length ? Math.max(...cycles.map((cd) => parseInt(String(cd.cycleNum), 10) || 0), 1) : 0;
      setCurrentCycle(maxCycleFromData + 1);
    } catch (error) {
      console.error('Error loading cycle data:', error);
    }
  };

  useLayoutEffect(() => {
    if (!plantTourId || !isOfflineStarted) return;
    const fetched = store.getState()?.proofing?.fetchedCycles ?? [];
    if (!Array.isArray(fetched) || fetched.length === 0) return;
    const byCycle: ProofingCycleData[][] = Array(MAX_CYCLES).fill(null).map(() => []);
    fetched.forEach((cycleData: ProofingCycleData) => {
      const cycleNumStr = String(cycleData.cycleNum || '1').replace('Cycle-', '').trim();
      const cycleNum = parseInt(cycleNumStr, 10);
      if (isNaN(cycleNum) || cycleNum < 1) return;
      const i = idx(cycleNum);
      if (i >= 0 && i < MAX_CYCLES) {
        byCycle[i].push({
          cycleNum: String(cycleNum),
          product: cycleData.product || '',
          executiveName: cycleData.executiveName || '',
          lineNumber: cycleData.lineNumber || '',
          observedtime: cycleData.observedtime || '',
          standardProofingTime: cycleData.standardProofingTime || '',
          rows: cycleData.rows || [],
        });
      }
    });
    setSavedCycles(byCycle);
    setCycleCompleted(byCycle.map((sessions) => sessions.length > 0));
    const maxCycleFromData = byCycle.reduce((max, sessions, index) => (sessions.length > 0 ? Math.max(max, index + 1) : max), 0);
    setCurrentCycle(maxCycleFromData + 1);
  }, [plantTourId, isOfflineStarted]);

  useEffect(() => {
    if (!plantTourId) return;
    if (isOfflineStarted) {
      const cyclesToUse = store.getState()?.proofing?.fetchedCycles ?? [];
      if (!Array.isArray(cyclesToUse) || cyclesToUse.length === 0) {
        setSavedCycles(Array(MAX_CYCLES).fill(null).map(() => []));
        setCycleCompleted(Array(MAX_CYCLES).fill(false));
        setCurrentCycle(1);
        return;
      }
      const byCycle: ProofingCycleData[][] = Array(MAX_CYCLES).fill(null).map(() => []);
      cyclesToUse.forEach((cycleData: ProofingCycleData) => {
        const cycleNumStr = String(cycleData.cycleNum || '1').replace('Cycle-', '').trim();
        const cycleNum = parseInt(cycleNumStr, 10);
        if (isNaN(cycleNum) || cycleNum < 1) return;
        const i = idx(cycleNum);
        if (i >= 0 && i < MAX_CYCLES) {
          byCycle[i].push({
            cycleNum: String(cycleNum),
            product: cycleData.product || '',
            executiveName: cycleData.executiveName || '',
            lineNumber: cycleData.lineNumber || '',
            observedtime: cycleData.observedtime || '',
            standardProofingTime: cycleData.standardProofingTime || '',
            rows: cycleData.rows || [],
          });
        }
      });
      setSavedCycles(byCycle);
      setCycleCompleted(byCycle.map((sessions) => sessions.length > 0));
      const maxCycleFromData = byCycle.reduce((max, sessions, index) => (sessions.length > 0 ? Math.max(max, index + 1) : max), 0);
      setCurrentCycle(maxCycleFromData + 1);
      return;
    }
    loadCycleData();
  }, [plantTourId, isOfflineStarted, reduxFetchedCycles?.length]);

  useEffect(() => {
    const initial = getProofingInitial();
    if (!initial?.productName && !initial?.executiveName && !initial?.lineNumber && !initial?.standardProofingTime) return;
    setFormDataByCycle((prev) =>
      prev.map((p, i) => {
        const cycleNum = i + 1;
        if (cycleNum === 1) {
          if (p.productName || p.executiveName || p.lineNumber || p.standardProofingTime) return p;
          return { productName: initial?.productName ?? '', executiveName: initial?.executiveName ?? '', lineNumber: initial?.lineNumber ?? '', standardProofingTime: initial?.standardProofingTime ?? '' };
        }
        if (!cycleCompleted[cycleNum - 2]) return p;
        if (p.productName || p.executiveName || p.lineNumber || p.standardProofingTime) return p;
        return { productName: initial?.productName ?? '', executiveName: initial?.executiveName ?? '', lineNumber: initial?.lineNumber ?? '', standardProofingTime: initial?.standardProofingTime ?? '' };
      })
    );
  }, [cycleCompleted]);

  const headerDate = moment().format('DD/MM/YYYY');
  const shiftValue = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('shiftValue') || 'Shift 1' : 'Shift 1';

  const renderSummaryTable = (session: ProofingCycleData) => (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border border-gray-200">
        <thead>
          <tr className="bg-gray-50">
            <th className="border border-gray-200 px-3 py-2 text-left font-medium text-gray-700">Variety</th>
            <th className="border border-gray-200 px-3 py-2 text-left font-medium text-gray-700">Proofing – In Time</th>
            <th className="border border-gray-200 px-3 py-2 text-left font-medium text-gray-700">Proofing – Out Time</th>
            <th className="border border-gray-200 px-3 py-2 text-left font-medium text-gray-700">Proofer Temp</th>
            <th className="border border-gray-200 px-3 py-2 text-left font-medium text-gray-700">RH% Proofer</th>
          </tr>
        </thead>
        <tbody>
          {(session.rows || []).map((row, index) => (
            <tr key={index}>
              <td className="border border-gray-200 px-3 py-2 text-gray-900">{row.variety || '-'}</td>
              <td className="border border-gray-200 px-3 py-2 text-gray-900">{row.proofingInTime || '-'}</td>
              <td className="border border-gray-200 px-3 py-2 text-gray-900">{row.proofingOutTime || '-'}</td>
              <td className="border border-gray-200 px-3 py-2 text-gray-900">{row.prooferTemp || '-'}</td>
              <td className="border border-gray-200 px-3 py-2 text-gray-900">{row.rhProofer || '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <DashboardLayout>
      <div className="w-full px-4 md:px-6 py-6">
        <div className="mb-4">
          <button type="button" onClick={handleBack} className="flex items-center gap-1 text-gray-800 hover:text-gray-600 font-semibold bg-transparent border-0 cursor-pointer p-0">
            <span className="text-lg">←</span>
            <span>Back</span>
          </button>
        </div>
        <div className="flex items-center justify-between px-4 py-3 mb-6 rounded-lg bg-gray-100 border border-gray-200">
          <h1 className="text-xl font-bold text-gray-800">Proofing</h1>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-200 text-gray-800 text-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-gray-500" />
              {shiftValue}
            </span>
            <span className="text-gray-500">•</span>
            <span className="text-sm text-gray-800">{headerDate}</span>
            <button type="button" className="p-1 rounded hover:bg-gray-200 text-gray-600" aria-label="More options">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" /></svg>
            </button>
          </div>
        </div>

        <div className="w-full px-4 md:px-6 space-y-6">
          {Array.from({ length: MAX_CYCLES }, (_, i) => i + 1)
            .filter((n) => cycleCompleted[idx(n)] || savedCycles[idx(n)].length > 0)
            .map((n) => (
              <div key={n} className="light-surface bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
                <div className="flex items-center justify-between p-4 cursor-pointer border-b border-gray-200" onClick={() => toggleCompletedCycleExpansion(n)}>
                  <div className="flex items-center gap-3">
                    <h2 className="text-lg font-bold text-gray-800">Cycle {n}</h2>
                    <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                    </div>
                  </div>
                  <svg className={`w-5 h-5 text-gray-500 transition-transform ${expandedCompletedCycles.has(n) ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </div>
                {expandedCompletedCycles.has(n) && savedCycles[idx(n)].length > 0 && (
                  <div className="p-4 space-y-6">
                    {savedCycles[idx(n)].map((session, index) => (
                      <div key={index} className="border-b border-gray-200 pb-6 last:border-b-0">
                        <div className="bg-blue-50 rounded-lg p-4 mb-4">
                          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                            <div><span className="text-gray-600">Product: </span><span className="font-semibold text-gray-800">{session.product || '-'}</span></div>
                            <div><span className="text-gray-600">Executive Name: </span><span className="font-semibold text-gray-800">{session.executiveName || '-'}</span></div>
                            <div><span className="text-gray-600">Line Number: </span><span className="font-semibold text-gray-800">{session.lineNumber || '-'}</span></div>
                            <div><span className="text-gray-600">Date & Time: </span><span className="font-semibold text-gray-800">{session.observedtime || '-'}</span></div>
                            <div><span className="text-gray-600">Standard proofing Time: </span><span className="font-semibold text-gray-800">{session.standardProofingTime || '-'}</span></div>
                          </div>
                        </div>
                        <div className="bg-red-50 rounded-t-lg px-4 py-2"><h3 className="text-sm font-bold text-red-700">Summary</h3></div>
                        <div className="border border-t-0 border-gray-200 rounded-b-lg p-4">{renderSummaryTable(session)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}

          <div className="light-surface bg-white rounded-lg shadow-md border border-gray-200 p-6">
            {/* Start section */}
            {!isSessionStarted[idx(currentCycle)] && !cycleCompleted[idx(currentCycle)] && (savedCycles[idx(currentCycle)].length === 0 || showStartFormForNewProduct[idx(currentCycle)]) && (
              <div className="mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Product Name</label>
                    <input type="text" name="productName" value={formDataByCycle[idx(currentCycle)].productName} onChange={(e) => handleInputChange(e, currentCycle, 'start')} className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Enter value" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Executive Name</label>
                    <input type="text" name="executiveName" value={formDataByCycle[idx(currentCycle)].executiveName} onChange={(e) => handleInputChange(e, currentCycle, 'start')} className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Enter value" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Line Number</label>
                    <input type="text" name="lineNumber" value={formDataByCycle[idx(currentCycle)].lineNumber} onChange={(e) => handleInputChange(e, currentCycle, 'start')} className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Enter value" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Date & Time</label>
                    <input type="text" readOnly value={observedtimeByCycle[idx(currentCycle)] || formatObservedTime()} className="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-100 text-gray-700" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Standard proofing Time</label>
                    <input
                      type="time"
                      name="standardProofingTime"
                      value={formDataByCycle[idx(currentCycle)].standardProofingTime}
                      onChange={(e) => handleInputChange(e, currentCycle, 'start')}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div className="flex justify-end">
                  <button onClick={() => handleStartSession(currentCycle)} className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium">Start Session</button>
                </div>
              </div>
            )}

            {/* Completed current cycle */}
            {cycleCompleted[idx(currentCycle)] && (
              <>
                <div className="flex items-center justify-between mb-4 cursor-pointer" onClick={() => setCycleExpandedAt(currentCycle, !cycleExpanded[idx(currentCycle)])}>
                  <div className="flex items-center gap-3">
                    <h2 className="text-lg font-bold text-gray-800">Cycle {currentCycle}</h2>
                    <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                    </div>
                  </div>
                  <svg className={`w-5 h-5 text-gray-500 ${cycleExpanded[idx(currentCycle)] ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </div>
                {cycleExpanded[idx(currentCycle)] && (
                  <div className="space-y-6">
                    {savedCycles[idx(currentCycle)].map((session, index) => (
                      <div key={index} className="border-b border-gray-200 pb-6 last:border-b-0">
                        <div className="bg-blue-50 rounded-lg p-4 mb-4">
                          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                            <div><span className="text-gray-600">Product: </span><span className="font-semibold text-gray-800">{session.product || '-'}</span></div>
                            <div><span className="text-gray-600">Executive Name: </span><span className="font-semibold text-gray-800">{session.executiveName || '-'}</span></div>
                            <div><span className="text-gray-600">Line Number: </span><span className="font-semibold text-gray-800">{session.lineNumber || '-'}</span></div>
                            <div><span className="text-gray-600">Date & Time: </span><span className="font-semibold text-gray-800">{session.observedtime || '-'}</span></div>
                            <div><span className="text-gray-600">Standard proofing Time: </span><span className="font-semibold text-gray-800">{session.standardProofingTime || '-'}</span></div>
                          </div>
                        </div>
                        <div className="bg-red-50 rounded-t-lg px-4 py-2"><h3 className="text-sm font-bold text-red-700">Summary</h3></div>
                        <div className="border border-t-0 border-gray-200 rounded-b-lg p-4">{renderSummaryTable(session)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* After save: Add new product / Complete Cycle */}
            {savedCycles[idx(currentCycle)].length > 0 && !cycleCompleted[idx(currentCycle)] && !isSessionStarted[idx(currentCycle)] && !showStartFormForNewProduct[idx(currentCycle)] && (
              <div className="mb-6">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-green-700 font-semibold mb-3">{savedCycles[idx(currentCycle)].length} product(s) saved in Cycle {currentCycle}</p>
                  <div className="flex flex-wrap gap-2">
                    <button type="button" onClick={() => handleAddNewProduct(currentCycle)} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium">Add new product</button>
                    <button type="button" onClick={() => handleCompleteCycle(currentCycle)} className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm font-medium">Complete Cycle & Move to Next</button>
                  </div>
                </div>
              </div>
            )}

            {/* Save section */}
            {isSessionStarted[idx(currentCycle)] && !cycleCompleted[idx(currentCycle)] && (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-gray-800">Cycle {currentCycle}</h2>
                  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </div>
                <div className="bg-blue-50 rounded-lg p-4 mb-6">
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                    <div><div className="text-xs text-gray-600 mb-1">Product</div><div className="font-semibold text-gray-800">{formDataByCycle[idx(currentCycle)].productName || '-'}</div></div>
                    <div><div className="text-xs text-gray-600 mb-1">Executive Name</div><div className="font-semibold text-gray-800">{formDataByCycle[idx(currentCycle)].executiveName || '-'}</div></div>
                    <div><div className="text-xs text-gray-600 mb-1">Line Number</div><div className="font-semibold text-gray-800">{formDataByCycle[idx(currentCycle)].lineNumber || '-'}</div></div>
                    <div><div className="text-xs text-gray-600 mb-1">Date & Time</div><div className="font-semibold text-gray-800">{observedtimeByCycle[idx(currentCycle)] || '-'}</div></div>
                    <div><div className="text-xs text-gray-600 mb-1">Standard proofing Time</div><div className="font-semibold text-gray-800">{formDataByCycle[idx(currentCycle)].standardProofingTime || '-'}</div></div>
                  </div>
                </div>
                <h3 className="text-base font-bold text-gray-800 mb-4">Proofing Observations</h3>
                <div className="overflow-x-auto mb-6">
                <div className="grid grid-cols-5 gap-4 items-end min-w-[52rem]">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Proofing – In Time</label>
                    <input
                      type="time"
                      name="proofingInTime"
                      value={saveFormDataByCycle[idx(currentCycle)].proofingInTime}
                      onChange={(e) => handleInputChange(e, currentCycle, 'save')}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Proofing – Out Time</label>
                    <input
                      type="time"
                      name="proofingOutTime"
                      value={saveFormDataByCycle[idx(currentCycle)].proofingOutTime}
                      onChange={(e) => handleInputChange(e, currentCycle, 'save')}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Variety</label>
                    <input
                      type="text"
                      name="variety"
                      value={saveFormDataByCycle[idx(currentCycle)].variety}
                      onChange={(e) => handleInputChange(e, currentCycle, 'save')}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter value"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Temperature of Proofer</label>
                    <input
                      type="text"
                      name="prooferTemp"
                      value={saveFormDataByCycle[idx(currentCycle)].prooferTemp}
                      onChange={(e) => handleInputChange(e, currentCycle, 'save')}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter value"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">RH% Proofer</label>
                    <input
                      type="text"
                      name="rhProofer"
                      value={saveFormDataByCycle[idx(currentCycle)].rhProofer}
                      onChange={(e) => handleInputChange(e, currentCycle, 'save')}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter value"
                    />
                  </div>
                </div>
                </div>
                <div className="flex justify-end gap-4">
                    <button onClick={() => handleCancelSession(currentCycle)} className="px-6 py-2 border border-blue-300 text-blue-600 bg-white rounded-md hover:bg-blue-50 font-medium">Cancel</button>
                    <button onClick={() => handleSaveSession(currentCycle)} className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium">Save Session</button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Proofing;
